import React, { useState } from 'react';
import {
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  IconButton,
  Slider,
  Card,
  CardContent,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import stylingService from '../../services/stylingService';

const StylingPanel = ({ settings, stylings, refreshStylings }) => {
  const [showStyleForm, setShowStyleForm] = useState(false);
  const [editingStyle, setEditingStyle] = useState(null);
  const [styleForm, setStyleForm] = useState({
    name: '',
    width: 100,
    height: 100,
    borderRadius: 10
  });

  // Generic form change handler
  const handleStyleChange = (field) => (event) => {
    setStyleForm(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  // Slider change handlers
  const handleSliderChange = (field) => (_, newValue) => {
    setStyleForm(prev => ({
      ...prev,
      [field]: newValue
    }));
  };

  // Style operations
  const handleAddStyle = () => {
    setEditingStyle(null);
    setStyleForm({
      name: '',
      width: 100,
      height: 100,
      borderRadius: 10
    });
    setShowStyleForm(true);
  };

  const handleEditStyle = (style) => {
    setEditingStyle(style._id);
    setStyleForm({
      name: style.name,
      width: style.width,
      height: style.height,
      borderRadius: style.borderRadius
    });
    setShowStyleForm(true);
  };

  const handleCancelStyle = () => {
    setShowStyleForm(false);
    setEditingStyle(null);
  };

  const handleSaveStyle = async () => {
    if (!styleForm.name) {
      alert('Stil adı gereklidir');
      return;
    }

    try {
      if (editingStyle) {
        await stylingService.updateStyling(editingStyle, styleForm);
      } else {
        await stylingService.createStyling(settings._id, {
          ...styleForm,
          timeline_id: settings._id
        });
      }

      setShowStyleForm(false);
      await refreshStylings();
      
      // Dispatch custom event to notify timeline of style changes
      document.dispatchEvent(new CustomEvent('timelineDataUpdated'));
    } catch (error) {
      console.error('Error saving style:', error);
      alert('Stil kaydedilirken hata oluştu');
    }
  };

  const handleDeleteStyle = async (styleId) => {
    if (!window.confirm('Bu stili silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await stylingService.deleteStyling(styleId);
      await refreshStylings();
      
      // Dispatch custom event to notify timeline of style changes
      document.dispatchEvent(new CustomEvent('timelineDataUpdated'));
    } catch (error) {
      console.error('Error deleting style:', error);
      alert('Stil silinirken hata oluştu');
    }
  };

  return (
    <>
      {/* Button to add new style or form heading */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          {showStyleForm ? 
            (editingStyle ? 'Stili Düzenle' : 'Yeni Stil Ekle') : 
            'Stiller'}
        </Typography>
        {!showStyleForm && (
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleAddStyle}
            startIcon={<ViewInArIcon />}
          >
            Stil Ekle
          </Button>
        )}
      </Box>
      
      {/* Style form - shown only when adding or editing */}
      {showStyleForm ? (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Stil Adı"
              fullWidth
              margin="normal"
              value={styleForm.name}
              onChange={handleStyleChange('name')}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Typography gutterBottom>Genişlik: {styleForm.width}%</Typography>
            <Slider
              value={styleForm.width}
              onChange={handleSliderChange('width')}
              valueLabelDisplay="auto"
              min={50}
              max={500}
              step={5}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Typography gutterBottom>Yükseklik: {styleForm.height}%</Typography>
            <Slider
              value={styleForm.height}
              onChange={handleSliderChange('height')}
              valueLabelDisplay="auto"
              min={5}
              max={500}
              step={5}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Typography gutterBottom>Köşe Yuvarlaklığı: {styleForm.borderRadius}px</Typography>
            <Slider
              value={styleForm.borderRadius}
              onChange={handleSliderChange('borderRadius')}
              valueLabelDisplay="auto"
              min={0}
              max={Math.min(styleForm.height,styleForm.width)/2}
              step={1}
            />
          </Grid>
          
          {/* Preview of the style */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>Önizleme:</Typography>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              p: 2, 
              bgcolor: '#f5f5f5',
              borderRadius: 1,
            }}>
              <Box sx={{ 
                width: `${styleForm.width}px`, 
                height: `${styleForm.height}px`,
                borderRadius: `${styleForm.borderRadius}px`,
                bgcolor: '#e0e0e0',
                border: '1px solid #ccc',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                p: 2,
              }}>
                <Typography variant="body2" align="center">
                  {styleForm.name || 'Önizleme'}
                </Typography>
              </Box>
            </Box>
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button 
                variant="outlined" 
                onClick={handleCancelStyle}
                sx={{ mr: 1 }}
              >
                İptal
              </Button>
              <Button 
                variant="contained" 
                color="primary"
                onClick={handleSaveStyle}
              >
                {editingStyle ? 'Stili Güncelle' : 'Stil Ekle'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      ) : (
        /* List of existing styles - shown when not adding/editing */
        <Box>
          {!stylings || stylings.length === 0 ? (
            <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
              Henüz stil oluşturulmadı
            </Typography>
          ) : (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {stylings.map((style) => (
                <Grid item xs={12} sm={6} md={4} key={style._id}>
                  <Card elevation={2}>
                    <CardContent>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        mb: 2
                      }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {style.name}
                        </Typography>
                        <Box>
                          <Tooltip title="Düzenle">
                            <IconButton 
                              size="small" 
                              onClick={() => handleEditStyle(style)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Sil">
                            <IconButton 
                              size="small" 
                              onClick={() => handleDeleteStyle(style._id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        p: 1, 
                        bgcolor: '#f5f5f5',
                        borderRadius: 1,
                        height: 100
                      }}>
                        <Box sx={{ 
                          width: style.width * 0.5, 
                          height: style.height * 0.5,
                          borderRadius: `${style.borderRadius}px`,
                          bgcolor: '#e0e0e0',
                          border: '1px solid #ccc',
                          maxWidth: '90%',
                          maxHeight: '90%'
                        }} />
                      </Box>
                      
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          Genişlik: {style.width}% • Yükseklik: {style.height}% • Kenar: {style.borderRadius}px
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}
    </>
  );
};

export default StylingPanel; 